import { jwtDecode } from 'jwt-decode';
import { SessionState } from '../../store/slices/sessionSlice';

const TOKEN_KEY = 'token';

// ── character-substitution tables (ported from Angular's CommonService) ──────

const SYMBOL_TO_LETTER: Record<string, string> = {
  '!': 'a',
  '@': 'B',
  '#': 'c',
  $: 'D',
  '%': 'e',
  '^': 'F',
  '&': 'g',
  '?': 'i',
  '+': 'J',
  '=': 'k',
  '|': 'L',
  '~': 'm',
  '/': 'N',
  '-': 'o',
  _: 'P',
  '{': 'q',
  '}': 'R',
  '[': 's',
  ']': 'T',
  '(': 'U',
  ')': 'v',
  '<': 'W',
  '>': 'x',
  ':': 'Y',
  ';': 'z',
  '*': '.',
};

// Inverse of SYMBOL_TO_LETTER — used to re-encode a JWT back to the raw
// custom format the backend issues and the app stores in localStorage.
const LETTER_TO_SYMBOL: Record<string, string> = Object.fromEntries(
  Object.entries(SYMBOL_TO_LETTER).map(([sym, letter]) => [letter, sym])
);

const ALPHA_TO_NUMBER: Record<string, string> = {
  A: '0',
  b: '1',
  C: '2',
  d: '3',
  E: '4',
  f: '5',
  G: '6',
  h: '7',
  I: '8',
  j: '9',
};

// Equivalent of Angular's CommonService.assignLetters()
// Decodes: raw custom token → valid JWT string
export function assignLetters(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += SYMBOL_TO_LETTER[text[i]] ?? text[i];
  }
  return result;
}

// Inverse of assignLetters()
// Encodes: valid JWT string → raw custom token (for storing back in localStorage)
export function reverseAssignLetters(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += LETTER_TO_SYMBOL[text[i]] ?? text[i];
  }
  return result;
}

// Equivalent of Angular's CommonService.alphabetToNumber()
export function alphabetToNumber(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += ALPHA_TO_NUMBER[text[i]] ?? text[i];
  }
  return result;
}

// Equivalent of Angular's CommonService.decryptionData()
// Reverses the backend's symbol-substitution to produce a valid JWT string
export function decryptionData(token: string): string {
  return assignLetters(token);
}

// ── Session extension (Case 4) ───────────────────────────────────────────────

/**
 * Bumps the `exp` field inside the stored token by +1 hour and writes
 * the re-encoded token back to localStorage — silently, no API call.
 *
 * Flow:
 *  raw token → assignLetters() → JWT → split on '.'
 *  → base64url-decode payload → JSON → bump exp by 3600s
 *  → re-encode payload → reassemble JWT
 *  → reverseAssignLetters() → store back as 'token'
 *
 * Also updates the sessionSlice's tokenExpiry via a dispatched action if
 * you pass the Redux store, but the localStorage write alone is enough for
 * all subsequent API calls and expiry checks.
 */
export function refreshTokenExpiry(): void {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return;

    // Step 1: decode raw → JWT
    const jwt = assignLetters(raw);
    const parts = jwt.split('.');
    if (parts.length !== 3) return;

    const [header, payloadB64, signature] = parts;

    // Step 2: base64url → JSON → parse
    // base64url uses '-' and '_' instead of '+' and '/'
    const base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const jsonStr = atob(base64);
    const payload = JSON.parse(jsonStr) as Record<string, unknown>;

    if (typeof payload.exp !== 'number') return;

    // Step 3: bump exp by 1 hour (3600 seconds)
    payload.exp = payload.exp + 3600;

    // Step 4: re-encode payload back to base64url
    const newPayloadJson = JSON.stringify(payload);
    const newPayloadB64 = btoa(newPayloadJson)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, ''); // base64url: strip padding

    // Step 5: reassemble the JWT with the original header & signature
    const newJwt = `${header}.${newPayloadB64}.${signature}`;

    // Step 6: re-encode back to the raw custom format
    const newRawToken = reverseAssignLetters(newJwt);

    // Step 7: silently store back — no UI change, no API call
    localStorage.setItem(TOKEN_KEY, newRawToken);
  } catch (err) {
    console.error('[Session] refreshTokenExpiry failed:', err);
  }
}

/**
 * Returns true when the stored token's exp is in the past (or token missing).
 * Used on app load to detect Cases 1 & 2.
 */
export function isSessionExpired(): boolean {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return true; // Case 2 — no token

  try {
    const jwt = assignLetters(raw);
    const decoded = jwtDecode<{ exp?: number }>(jwt);
    if (!decoded.exp) return true;
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
}

// ── main decode function ─────────────────────────────────────────────────────

export function decodeSessionFromStorage(): Omit<
  SessionState,
  'loaded'
> | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    // console.log('raw', raw);
    if (!raw) return null;

    // Step 1: reverse symbol-substitution → valid JWT
    const jwt = decryptionData(raw);

    // Step 2: decode JWT payload (no signature verification)
    const d = jwtDecode<Record<string, unknown>>(jwt);

    const usertypeid = String(d['emis_usertype'] ?? '');
    const isOfficeUser = [5, 6, 9, 10, 22].includes(Number(usertypeid));
    if (isOfficeUser && d['off_key_id']) {
      localStorage.setItem('officeKeyID', String(d['off_key_id']));
    }

    return {
      // identity
      userId: Number(d['emis_user_id'] ?? d['user_id']) || 0,
      username: String(d['emis_username'] ?? ''),
      emis_username: String(d['emis_username'] ?? ''),
      name: String(d['name'] ?? ''),
      teacher_name: String(d['teacher_name'] ?? ''),
      Socialname: String(d['Socialname'] ?? ''),
      designation: String(d['designation'] ?? ''),
      phnumber: String(d['mbl_nmbr'] ?? ''),
      email: String(d['email'] ?? ''),

      // school
      schoolName: String(d['school_name'] ?? ''),
      schoolId: Number(d['school_id']) || 0,
      schoolKeyId: String(d['school_key_id'] ?? ''),
      schoolTypeId: Number(d['school_type_id']) || 0,
      schlId: String(d['schlId'] ?? ''),
      highClass: Number(d['high_class']) || 0,
      lowClass: Number(d['low_class']) || 0,

      // teacher
      teacherId: Number(d['teacher_id']) || 0,
      teacherType: String(d['teacher_type'] ?? ''),
      type_teacher: String(d['type_teacher'] ?? ''),
      subjects: String(d['subjects'] ?? ''),
      subject_id: String(d['subject_id'] ?? ''),

      // user type / role
      usertype: String(d['user_type'] ?? ''),
      usertypeid,
      emisUsertype1: String(d['emis_usertype1'] ?? ''),
      roleId: Number(d['roleId']) || 0,
      offKeyId: isOfficeUser ? String(d['off_key_id'] ?? '') : '',

      // category
      cate_id: String(d['cate_id'] ?? ''),
      catty_id: String(d['catty_id'] ?? ''),
      cateType: String(d['cate_type'] ?? ''),
      category_type: String(d['category_type'] ?? ''),
      manage_id: String(d['manage_id'] ?? ''),

      // location
      districtId: String(d['district_id'] ?? ''),
      districtName: String(d['district_name'] ?? ''),
      blockId: String(d['block_id'] ?? ''),
      blockName: String(d['block_name'] ?? ''),
      edu_dist_name: String(d['edu_dist_name'] ?? ''),
      edn_dist_name: String(d['edn_dist_name'] ?? ''),
      // Angular: edn_dist_id takes priority over edu_district_id
      edu_district_id: String(d['edn_dist_id'] ?? d['edu_district_id'] ?? ''),

      // module permissions
      mod2: String(d['mod2'] ?? ''),

      // switch login
      switch_login: String(d['swith_login'] ?? ''), // note: typo in JWT key
      switch_designation: String(d['switch_designation'] ?? ''),

      // token / meta — authToken is the decoded JWT (same as Angular)
      authToken: jwt,
      iat: Number(d['iat']) || 0,
      tokenExpiry: Number(d['exp']) || 0,
    };
  } catch (err) {
    console.error('[Session] Failed to decode token:', err);
    return null;
  }
}
